import { FC, useEffect, useRef, useState } from 'react';
import { TopRow } from './TopRow';
import { Button, Stack, Table, TableBody, TableContainer, TableFooter, TableRow, Typography, styled } from '@mui/material';
import { NameRow } from './NameRow';
import { Phase } from '../JourneyDetailPage';
import { TableCell } from './TableCell';
import { FormattedMessage, useIntl } from 'react-intl';
import { Add } from '@mui/icons-material';
import { AddWidget } from './AddWidget';
import { Widget } from '../../../gql/graphql';
import { WidgetRow } from './WidgetRow';
import { graphql } from '../../../gql';
import { useApiMutation } from '../../../query/hooks';
import { ThemeMode } from '../../../types/config';
import { getNightOrDayPalette } from '../../../utils/getNightOrDayPalette';
import { useModalContext } from '../../../modal/ModalContext';
import { mutationResponseHandler } from '../../../utils/mutationResponseHandler';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import { useSnackbar } from '../../../store/snackbar';

const deleteWidgetMutation = graphql(`
	mutation journeyDetail_deleteWidget($id: UUID!) {
		deleteWidget(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

const updateWidgetOrderMutation = graphql(`
	mutation journeyDetail_updateWidgetsOrder($widgets: [JourneyUpdateWidgetsEntityRelationInput!], $journeyId: UUID!) {
		updateJourney(by: { id: $journeyId }, data: { widgets: $widgets }) {
			ok
			errorMessage
		}
	}
`);

export const MIN_NUMBER_OF_ACTIVITIES = 3;
export const WIDGETS_COLUMN_WIDTH = 209;
export const DND_DELAY = 300;

type Props = Readonly<{
	id: string;
	phases: readonly Phase[];
	widgets: readonly Widget[];
}>;

export const OddCol = styled('col')(({ theme }) => ({
	backgroundColor: theme.palette.mode === ThemeMode.DARK ? theme.palette.night[100] : '#F7F7F7',
}));

export const EvenCol = styled('col')(({ theme }) => ({
	backgroundColor: theme.palette[getNightOrDayPalette(theme)][0],
}));

const DetailTable: FC<Props> = ({ phases, id, widgets }) => {
	const intl = useIntl();
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const { mutate: removeWidget } = useApiMutation(deleteWidgetMutation);
	const { mutateAsync: updateWidgetOrder } = useApiMutation(updateWidgetOrderMutation);
	const [widgetsState, setWidgetsState] = useState(widgets);
	const { setModal } = useModalContext();
	const tableRef = useRef(null);
	const { openSnackbar } = useSnackbar();

	const handleEditWidgetName = (widgetId: string) => {
		setModal({ type: 'editWidgetName', payload: { id: widgetId }, maxWidth: 'xs' });
	};

	useEffect(() => {
		setWidgetsState(widgets);
	}, [widgets]);

	const handleDeleteWidget = (widgetId: string) => {
		removeWidget(
			{ id: widgetId },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.deleteWidget.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'journey.detail.widget.delete.success' }),
							error: intl.formatMessage({ id: 'journey.detail.widget.delete.error' }),
						},
						errorMessage: result.deleteWidget.errorMessage,
						openSnackbar,
					});
				},
			},
		);
	};

	const handleReorderWidgets = async (result: DropResult) => {
		const { source, destination } = result;

		if (destination == null) {
			return;
		}

		const newState = [...widgetsState];
		const itemToInsertToNewPosition = newState[source.index];

		newState.splice(source.index, 1);
		newState.splice(destination.index, 0, itemToInsertToNewPosition);

		setWidgetsState(newState);

		await updateWidgetOrder({
			widgets: newState.map((w, index) => ({
				update: {
					by: {
						id: w.id,
					},
					data: {
						order: index,
					},
				},
			})),
			journeyId: id,
		});
	};

	const onDragEnd = async (result: DropResult) => {
		const { source, destination } = result;

		if (!destination) {
			return;
		}

		if (destination.index === source.index) {
			return;
		}

		if (destination.index !== source.index) {
			await handleReorderWidgets(result);
			return;
		}
	};

	const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = useState(false);

	const [height, setHeight] = useState(0);
	useEffect(() => {
		const table = tableRef.current;
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (table) {
			const resizeObserver = new ResizeObserver((data) => {
				setHeight(data[0].contentRect.height);
			});

			resizeObserver.observe(table);

			return () => {
				resizeObserver.unobserve(table);
			};
		}
	});

	return (
		<TableContainer ref={tableContainerRef} sx={{ overflowX: 'unset' }}>
			<Table
				ref={tableRef}
				id={'detail-table'}
				sx={{
					borderCollapse: 'separate',
					height: '100%',
					position: 'relative',
					width: phases.length === 0 ? WIDGETS_COLUMN_WIDTH : '100%',
				}}
			>
				<colgroup>
					<col span={1} />
					{phases.map((phase, index) =>
						index % 2 === 0 ? (
							<EvenCol span={phase.touchpoints.length} key={phase.id} />
						) : (
							<OddCol span={phase.touchpoints.length} key={phase.id} />
						),
					)}
				</colgroup>

				<TopRow phasesData={phases} tableHeight={height} extendedOffset={true} />
				<NameRow journeyId={id} phases={phases} isFirst />

				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId={id}>
						{(provided) => (
							<TableBody ref={provided.innerRef} {...provided.droppableProps}>
								{(widgetsState as Widget[]).map((widget, index) => {
									return (
										<WidgetRow
											key={widget.id}
											phases={phases}
											journeyWidget={widget}
											index={index}
											handleDelete={() => handleDeleteWidget(widget.id)}
											handleEditName={() => handleEditWidgetName(widget.id)}
											isFirst={index === 0}
											isLast={index === widgetsState.length - 1}
										/>
									);
								})}
							</TableBody>
						)}
					</Droppable>
				</DragDropContext>

				<TableFooter data-ignore-pdf>
					<TableRow>
						<TableCell header isSticky>
							<Button variant={'outlined'} fullWidth type={'button'} onClick={() => setIsAddWidgetModalOpen(true)}>
								<Stack columnGap={0.5} width={'100%'} justifyContent={'center'} alignItems={'center'} flexDirection={'row'}>
									<Stack columnGap={0.5} width={'100%'} justifyContent={'center'} alignItems={'center'} flexDirection={'row'}>
										<Add fontSize={'small'} />
										<Typography sx={{ fontSize: 12 }}>
											<FormattedMessage id={'journey.detail.add-widget'} />
										</Typography>
									</Stack>
								</Stack>
							</Button>
						</TableCell>
					</TableRow>
				</TableFooter>
				<AddWidget open={isAddWidgetModalOpen} onClose={() => setIsAddWidgetModalOpen(false)} id={id} widgets={widgets} />
			</Table>
		</TableContainer>
	);
};

export { DetailTable };
